import { evaluate } from "mathjs"

export function textToFormula(formula, aFields, onFieldMatch) {
  const fieldMap = new Map()
  if (aFields && aFields.length > 0) {
    aFields.forEach((field) => fieldMap.set(field.opfd_nid.toString(), field.opfd_cname))
  }

  const result = []
  const operators = new Set(["+", "-", "*", "/", "(", ")", "[", "]", "{", "}"])
  const letters = new Set(
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZçÇãáéíóúàèìòùâêîôûäëïöü;:!?_&$%#@=\\<>|\"'`^~ºª§¹²³£¢€¥₩₿".split(
      "",
    ),
  )
  let buffer = ""
  let position = 0
  let insideQuotes = false

  if (!formula) {
    return []
  }
  
  if (typeof Number(formula) === 'number' && !isNaN(Number(formula))) {
    result.push({ value: formula, label: formula, position: position++, type: "NUMBER" })
    return result
  }

  for (let i = 0; i < formula.length; i++) {
    const char = formula[i]

    if (char === "'") {
      if (insideQuotes) {
        // End of quoted field ID
        buffer += char
        result.push(createToken(buffer, position++, fieldMap))
        buffer = ""
        insideQuotes = false
      } else {
        // Start of quoted field ID
        if (buffer.length > 0) {
          result.push(createToken(buffer, position++, fieldMap))
          buffer = ""
        }
        buffer += char
        insideQuotes = true
      }
    } else if (insideQuotes) {
      // Accumulate characters inside quotes
      buffer += char
    } else if ((char >= "0" && char <= "9") || char === "." || char === ",") {
      // Accumulate number characters
      buffer += char
    } else if (letters.has(char)) {
      // Check if the letter matches the beginning of any field name
      buffer += char // Accumulate letters to form variable names
      for (const field of aFields) {
        if (
          field.opfd_cname.toLowerCase().startsWith(buffer.toLowerCase()) &&
          onFieldMatch
        ) {
          onFieldMatch(field, buffer)
        }
      }
    } else if (operators.has(char)) {
      // Flush the buffer if not empty
      if (buffer.length > 0) {
        result.push(createToken(buffer, position++, fieldMap))
        buffer = ""
      }
      // Add operator
      if (char === "(" || char === ")") {
        result.push({
          value: char,
          label: char,
          position: position++,
          type: "PARENTHESES",
        })
      } else if (char === "[" || char === "]") {
        result.push({ value: char, label: char, position: position++, type: "BRACES" })
      } else if (char === "{" || char === "}") {
        result.push({ value: char, label: char, position: position++, type: "BRACKETS" })
      } else {
        // handle negative numbers (numbers that start with a minus sign)
        if (char === "-" && (operators.has(formula?.[i - 1]) || i === 0)) {
          buffer += char
        } else {
          result.push({ value: char, label: char, position: position++, type: "OPERATOR" })
        }
      }
    } else if (char === " ") {
      // Flush the buffer if not empty
      if (buffer.length > 0) {
        result.push(createToken(buffer, position++, fieldMap))
        buffer = ""
      }
    } else {
      // Handle unrecognized characters
      throw new Error(`Unrecognized character: ${char}`)
    }
  }

  // Flush the remaining buffer if not empty
  if (buffer.length > 0) {
    result.push(createToken(buffer, position++, fieldMap))
  }

  return result
}

function createToken(buffer, position, fieldMap) {
  if (buffer.startsWith("'") && buffer.endsWith("'")) {
    const fieldValue = buffer.slice(1, -1)
    const fieldName = fieldMap.get(fieldValue)
    if (fieldName !== undefined) {
      return { value: `'${fieldValue}'`, label: fieldName, position, type: "FIELD" }
    } else {
      return { value: buffer, label: buffer, position, type: "NUMBER" }
    }
  } else if (!isNaN(buffer.replace(",", "."))) {
    return { value: buffer, label: buffer, position, type: "NUMBER" }
  } else {
    return { value: buffer, label: buffer, position, type: "VARIABLE" } // Changed to variable type
  }
}

export function formulaToText(formula) {
  return (
    formula &&
    formula
      .map((token) => {
        if (token.type === "NUMBER") {
          return token.value
        }
        return token.label
      })
      .join(" ")
  )
}

function isBalanced(expression) {
  const stack = []
  const matchingBrackets = { ")": "(", "]": "[", "}": "{" }

  for (const char of expression) {
    if (["(", "[", "{"].includes(char)) {
      stack.push(char)
    } else if ([")", "]", "}"].includes(char)) {
      if (stack.pop() !== matchingBrackets[char]) {
        return false
      }
    }
  }

  return stack.length === 0
}

function endWithOperators(formula) {
  const operators = ["+", "-", "*", "/"]
  const lastChar = formula[formula.length - 1]
  return operators.includes(lastChar)
}

function lastCharBeforeGroup(formula) {
  const groups = ["]", ")", "}"]
  const operators = ["+", "-", "*", "/"]

  let resp = false
  for (let i = formula.length - 1; i >= 0; i--) {
    if (groups.includes(formula[i])) {
      resp = operators.includes(formula[i - 1])
    }
  }
  return resp
}

export function checkIfFormulaIsCorrect(formula, getValues) {
  try {
    const newFormula = changeVariablesToValueOnFormula(formula, [], getValues).replace(
      /<stop>/g,
      "0",
    )
    if (
      newFormula.match(/[a-zA-ZçÇãáéíóúàèìòùâêîôûäëïöü;:!?_&$%#@=\\<>|"`^~ºª§¹²³£¢€¥₩₿]/g)
    ) {
      return false
    }

    if (!isBalanced(newFormula)) {
      return false
    }
    if (endWithOperators(newFormula) || lastCharBeforeGroup(newFormula)) {
      return false
    }
    /* if (endWithOperators(newFormula)) {
      return false
    } */
    const correctedFormula = fixFormula(newFormula)
    evaluate(correctedFormula.replace(/,/g, "."))
    return true
  } catch (error) {
    return false
  }
}

export function fixFormula(formula) {
  if (!formula) {
    return ""
  }
  // Adiciona '*' entre números e agrupadores que estão diretamente ao lado.
  // Primeiro, trata casos de números seguidos por agrupadores.
  let result = formula.replace(/(\d)([([{])/g, "$1*$2")

  // Depois, trata casos de agrupadores seguidos por números.
  result = result.replace(/([)\]}])(\d)/g, "$1*$2")

  // Remove qualquer '*' desnecessário.
  result = result.replace(/\*\s*$/, "")

  return result
}

export function calculate(formula, callback) {
  try {
    if (!formula) {
      callback("")
      return ""
    }
    //check if exists <stop> on formula
    if (formula.includes("<stop>")) {
      callback("")
      return ""
    }
    const correctedFormula = fixFormula(formula)
    const result = evaluate(correctedFormula.replace(/,/g, "."))
    callback(result)
    return result
  } catch (error) {
    callback("")
  }
}

export function getCurrentField(ModelBoard, nFieldId) {
  return ModelBoard.fields.filter((field) => field.ombf_opfd_nid === Number(nFieldId))[0]
}

export function getVariablesOnFormula(formula) {
  if (!formula) return []
  const regex = /'([^']+)'/g
  const matches = formula.match(regex)

  const fieldsOnFormula = matches ? matches.map((match) => match.replace(/'/g, "")) : []

  return fieldsOnFormula
}

export function changeVariablesToValueOnFormula(formula, aFields, getValues) {
  const fieldsOnFormula = getVariablesOnFormula(formula)

  fieldsOnFormula.forEach((field) => {
    const selectField = aFields.find((f) => f.opfd_nid === Number(field))
    const formValue = getValues(`activity-field-${field}`)

    const selectedOption = selectField?.options.find(
      (option) => option.opfs_nid === Number(formValue),
    )

    let value = selectField?.opfd_bcalculate
      ? selectedOption?.opfs_coption_value || "<stop>"
      : formValue || "<stop>"

    formula = formula.replace(
      `'${field}'`,
      value ? value.toString().replace(",", ".") : "",
    )
  })
  return formula
}
